import React from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowUp, Linkedin, Github, Mail } from 'lucide-react';
import DotGrid from './DotGrid';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: '#',
      color: 'hover:text-blue-600'
    },
    {
      name: 'GitHub',
      icon: Github,
      url: '#',
      color: 'hover:text-gray-800 dark:hover:text-white'
    },
    {
      name: 'Email',
      icon: Mail,
      url: 'mailto:ezzouekhamza2411@gmail.com',
      color: 'hover:text-red-600'
    }
  ];

  return (
    <footer className="bg-dark-900 dark:bg-black text-white relative overflow-hidden">
      {/* Animation DotGrid en arrière-plan */}
      <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
        <DotGrid
          dotSize={8}
          gap={20}
          baseColor="#61dca3"
          activeColor="#61b3dc"
          proximity={100}
          shockRadius={200}
          shockStrength={3}
          resistance={750}
          returnDuration={1.5}
        />
      </div>

      <div className="container-custom relative z-10">
        <div className="py-12">
          {/* Contenu principal du footer */}
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Section marque */}
            <div>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-2xl font-bold gradient-text mb-4"
              >
                Ez-zouek Hamza
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-white font-bold mb-6"
              >
                Développeur Full Stack passionné par la création de solutions digitales innovantes. 
                Disponible pour de nouveaux projets et opportunités.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex space-x-4"
              >
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className={`w-10 h-10 rounded-lg bg-dark-800 flex items-center justify-center text-gray-400 transition-all duration-200 ${social.color}`}
                  >
                    <social.icon size={20} />
                  </motion.a>
                ))}
              </motion.div>
            </div>

            {/* Liens rapides */}
            <div>
              <motion.h4
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-lg font-semibold mb-4"
              >
                Liens rapides
              </motion.h4>
              <motion.ul
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-2"
              >
                {[
                  { name: 'À propos', href: '#about' },
                  { name: 'Expérience', href: '#experience' },
                  { name: 'Compétences', href: '#skills' },
                  { name: 'Projets', href: '#projects' },
                  { name: 'Contact', href: '#contact' }
                ].map((link, index) => (
                  <li key={link.name}>
                    <motion.a
                      href={link.href}
                      whileHover={{ x: 5 }}
                      className="text-white font-bold hover:text-primary-400 transition-colors duration-200"
                    >
                      {link.name}
                    </motion.a>
                  </li>
                ))}
              </motion.ul>
            </div>

            {/* Infos de contact */}
            <div>
              <motion.h4
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-lg font-semibold mb-4"
              >
                Infos de contact
              </motion.h4>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-3 text-white font-bold"
              >
                <p>ezzouekhamza2411@gmail.com</p>
                <p>06.40.34.70.45</p>
                <p>Casablanca, Maroc</p>
              </motion.div>
            </div>
          </div>

          {/* Séparateur */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent mb-8"
          />

          {/* Bas de page */}
          <div className="flex flex-col md:flex-row justify-between items-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-white font-bold text-sm mb-4 md:mb-0"
            >
              © 2024 Ez-zouek Hamza. Tous droits réservés. Réalisé avec{' '}
              <Heart className="inline-block w-4 h-4 text-red-500 animate-pulse" />
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollToTop}
              className="w-10 h-10 rounded-full bg-primary-600 hover:bg-primary-700 flex items-center justify-center text-white transition-all duration-200"
              title="Remonter en haut de la page"
            >
              <ArrowUp size={20} />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 